import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  HoverCard,
  Select,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { DashboardFilters } from "../lib/types";
import { useStagesQuery } from "@/shared/api";
import { ORDER_SOURCE, STAGE_STATUS_TEXT } from "@/features/assignment/lib";
import CustomDatePicker from "@/shared/ui/date-time-picker/date-time-picker";
import { getDefaultFilters } from "../lib/helpers";
import { useUpdateEffect } from "react-use";
import {
  MagnifyingGlassIcon,
  QuestionMarkCircledIcon,
} from "@radix-ui/react-icons";
import { useAnnotatorData } from "@/shared/hooks/useAnnotatorData";
import { VariantMultiSelect } from "./variant-multi-select";

export function SidebarFilters({
  filters,
  isInitialized,
  onApplyFilters,
}: {
  isInitialized: boolean;
  filters: DashboardFilters;
  onApplyFilters: (filters: DashboardFilters) => void;
}) {
  const { data: stages } = useStagesQuery();
  const [filtersForm, setFiltersForm] = useState(filters);
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const { annotatorOptions } = useAnnotatorData();

  useEffect(() => {
    if (!isInitialized) {
      setFiltersForm(filters);
    }
  }, [filters, isInitialized]);

  const stageStatusOptions = stages?.result.statuses?.map((stage) => ({
    label: STAGE_STATUS_TEXT[stage],
    value: stage,
  }));

  const orderSourceOptions = Object.values(ORDER_SOURCE).map((source) => ({
    label: source,
    value: source,
  }));

  const clearFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFiltersForm(defaultFilters);
    onApplyFilters(defaultFilters);
  };

  const applyFilters = () => {
    onApplyFilters(filtersForm);
  };

  useUpdateEffect(() => {
    setIsFiltersApplied(false);
  }, [filtersForm]);

  return (
    <Box className="min-w-[258px] w-[258px] pt-1 pl-4 pr-1">
      <Flex direction="column" gap="2">
        <Text weight="bold" as="p" size="3">
          Filter
        </Text>
        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Order ID
          </Text>
          <TextField.Root
            placeholder="Search by order ID"
            value={filtersForm.order_id || ""}
            onChange={(e) =>
              setFiltersForm({ ...filtersForm, order_id: e.target.value })
            }
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height="16" width="16" />
            </TextField.Slot>
          </TextField.Root>
        </Box>

        <Box>
          <Text
            as="p"
            size="2"
            weight="medium"
            className="mb-1 flex items-center gap-1"
          >
            SKU{" "}
            <HoverCard.Root>
              <HoverCard.Trigger>
                <Text>
                  <QuestionMarkCircledIcon />
                </Text>
              </HoverCard.Trigger>
              <HoverCard.Content maxWidth="372px">
                <Heading size="3" mb="2">
                  How to search for SKU
                </Heading>
                <Text size="2" as="p" mb="1">
                  Type anything → searches by Name & Barcode.
                </Text>
                <Text size="2" as="p">
                  For exact fields, use <strong>key:value</strong> (no spaces
                  around the colon)
                </Text>
                <Text size="2" as="div">
                  <ul className="list-disc pl-5">
                    <li>name:albeni</li>
                    <li>barcode:8690504091234</li>
                    <li>sku_id:48192</li>
                  </ul>
                </Text>
              </HoverCard.Content>
            </HoverCard.Root>
          </Text>
          <VariantMultiSelect
            value={filtersForm.sku_ids}
            onValueChange={(value) => {
              setFiltersForm({ ...filtersForm, sku_ids: value });
            }}
          />
        </Box>

        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Order Source
          </Text>
          <Select.Root
            value={filtersForm.order_source || ""}
            onValueChange={(value) =>
              setFiltersForm({ ...filtersForm, order_source: value })
            }
          >
            <Select.Trigger className="w-full"></Select.Trigger>
            <Select.Content>
              <Select.Item value="all">All</Select.Item>
              <Select.Separator />
              {orderSourceOptions?.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>

        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Current Assignee
          </Text>
          <Box>
            <Select.Root
              defaultValue="all"
              value={filtersForm.annotator_id || ""}
              onValueChange={(value) =>
                setFiltersForm({ ...filtersForm, annotator_id: value })
              }
            >
              <Select.Trigger className="w-full" />
              <Select.Content>
                <Select.Group>
                  <Select.Item value="all">All</Select.Item>
                  <Select.Separator />
                  {annotatorOptions?.length
                    ? annotatorOptions.map(({ key, value }) => (
                        <Select.Item key={key} value={key}>
                          {value}
                        </Select.Item>
                      ))
                    : null}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Box>
        </Box>

        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Stage Journey Annotation
          </Text>
          <Select.Root
            value={filtersForm.stage_journey_annotation || ""}
            onValueChange={(value) =>
              setFiltersForm({
                ...filtersForm,
                stage_journey_annotation: value,
              })
            }
          >
            <Select.Trigger className="w-full"></Select.Trigger>
            <Select.Content>
              <Select.Item value="all">All</Select.Item>
              <Select.Separator />
              {stageStatusOptions?.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>

        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Stage Transaction Annotation
          </Text>
          <Select.Root
            value={filtersForm.stage_transaction_annotation || ""}
            onValueChange={(value) =>
              setFiltersForm({
                ...filtersForm,
                stage_transaction_annotation: value,
              })
            }
          >
            <Select.Trigger className="w-full"></Select.Trigger>
            <Select.Content>
              <Select.Item value="all">All</Select.Item>
              <Select.Separator />
              {stageStatusOptions?.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>
        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Stage Track Persistence Annotation
          </Text>
          <Select.Root
            value={filtersForm.stage_track_persistence_annotation || ""}
            onValueChange={(value) =>
              setFiltersForm({
                ...filtersForm,
                stage_track_persistence_annotation: value,
              })
            }
          >
            <Select.Trigger className="w-full"></Select.Trigger>
            <Select.Content>
              <Select.Item value="all">All</Select.Item>
              <Select.Separator />
              {stageStatusOptions?.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>
        <Box>
          <Text as="p" size="2" weight="medium" mb="1">
            Journey Count
          </Text>
          <Flex align="center" gap="2">
            <Checkbox
              checked={filtersForm.journey_count_enabled}
              onCheckedChange={(checked) => {
                if (checked === true) {
                  setFiltersForm({
                    ...filtersForm,
                    journey_count_enabled: true,
                  });
                } else {
                  setFiltersForm({
                    ...filtersForm,
                    journeys_count: null,
                    journey_count_enabled: false,
                  });
                }
              }}
            />
            <TextField.Root
              disabled={!filtersForm.journey_count_enabled}
              value={filtersForm.journeys_count || ""}
              type="number"
              onChange={(e) =>
                setFiltersForm({
                  ...filtersForm,
                  journeys_count: e.target.value,
                })
              }
              className="grow"
            ></TextField.Root>
          </Flex>
        </Box>

        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">
            Date Range
          </Text>
          <Flex justify="between" align="center">
            <Box width="40px">
              <Text size="2">From:</Text>
            </Box>
            <Flex className="flex-1">
              <CustomDatePicker
                selected={filtersForm.from_datetime}
                onChange={(date) => {
                  setFiltersForm({
                    ...filtersForm,
                    from_datetime: date,
                  });
                }}
              />
            </Flex>
          </Flex>
          <Flex justify="between" align="center">
            <Box width="40px">
              <Text size="2">To:</Text>
            </Box>
            <Flex className="flex-1">
              <CustomDatePicker
                selected={filtersForm.to_datetime}
                onChange={(date) => {
                  setFiltersForm({
                    ...filtersForm,
                    to_datetime: date,
                  });
                }}
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex gap="2" mt="3">
          <Button onClick={clearFilters} variant="outline" className="flex-1">
            Clear
          </Button>
          <Button
            onClick={applyFilters}
            className="flex-1"
            disabled={isFiltersApplied}
          >
            Apply
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}
